import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import axios from 'axios';
import { toast } from './useToast';

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  contactNo: string;
  status: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthResponse {
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Create a configured axios instance for use in components
const createAuthenticatedAxios = () => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
  });

  // Add token to requests
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return instance;
};

// Add token to requests (except login and public endpoints)
axios.interceptors.request.use((config) => {
  // Don't add token to login, register, forgot-password, reset-password endpoints
  const publicEndpoints = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
  const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

  if (!isPublicEndpoint) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post('/auth/refresh', {
            refreshToken
          });
          const { accessToken } = response.data.data;
          localStorage.setItem('accessToken', accessToken);
          // Retry the original request
          error.config.headers.Authorization = `Bearer ${accessToken}`;
          return axios.request(error.config);
        } catch (refreshError) {
          // Refresh failed, logout user
          console.warn('Token refresh failed:', refreshError);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export function useAuth() {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const queryClient = useQueryClient();

  // Check if user is authenticated
  const { data: currentUser, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return null;

      const response = await axios.get('/api/users/profile');
      return response.data.data; // Backend returns user data in data field
    },
    enabled: !!localStorage.getItem('accessToken'),
    retry: false,
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<AuthResponse> => {
      try {
        const response = await axios.post('/auth/login', credentials);
        return response.data;
      } catch (error: any) {
        // Enhanced error handling
        if (error.response?.status === 401) {
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        } else if (error.response?.status === 403) {
          throw new Error('Your account has been disabled. Please contact an administrator.');
        } else if (error.response?.status === 429) {
          throw new Error('Too many login attempts. Please try again later.');
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
          throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
        } else {
          throw new Error(error.response?.data?.error?.message || error.response?.data?.message || 'Login failed. Please try again.');
        }
      }
    },
    onSuccess: (data) => {
      localStorage.setItem('accessToken', data.data.accessToken);
      localStorage.setItem('refreshToken', data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.data.user));
      setUser(data.data.user);
      queryClient.setQueryData(['auth', 'me'], data.data.user);
      toast({
        title: 'Login successful',
        description: `Welcome back, ${data.data.user.name}!`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Login failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Logout function
  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      queryClient.clear();
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
      // Force navigation to login page
      window.location.href = '/';
    }
  };

  // Update user state when query data changes
  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
      localStorage.setItem('user', JSON.stringify(currentUser));
    }
  }, [currentUser]);

  return {
    user: user || currentUser,
    isLoading,
    login: loginMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error?.message || null,
    logout,
    axios: createAuthenticatedAxios(), // Add axios instance to return value
  };
}